// angular
import { Component, HostListener, inject, signal } from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
// components
import { VideoPlayerComponent } from '@app/core/components/video-player/video-player.component';
import { SimpleVideoBoxComponent } from '@app/core/components/simple-video-box/simple-video-box.component';
// interfaces
import { IMedia } from '@app/shared/interfaces/media.interface';
// utils & pipes
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { DIALOG_DATA } from '@app/core/shared/utils/dialog/dialog-tokens';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { MediaImageFallbackDirective } from '@app/shared/directives/media-image-fallback.directive';
import { DialogConfig } from '@app/core/shared/services/dialog.service';

export function getGalleryDialogConfig(
  data: DialogConfig['data']
): DialogConfig {
  const isMobileView = window.matchMedia('(max-width: 47.99em)').matches;

  return {
    width: isMobileView ? '100vw' : 'auto',
    height: 'none',
    maxWidth: 'none',
    panelClass: 'overlay-panel--gallery-dialog',
    data,
  };
}

@Component({
  selector: 'pwj-gallery-dialog',
  standalone: true,
  imports: [
    NgClass,
    NgOptimizedImage,
    TranslateModule,
    VideoPlayerComponent,
    SimpleVideoBoxComponent,
    ImageSourcePipe,
    MediaImageFallbackDirective,
  ],
  templateUrl: './gallery-dialog.component.html',
  styleUrl: './gallery-dialog.component.scss',
})
export class GalleryDialogComponent {
  public title!: string;
  public selectedProductMedia!: IMedia;
  public mediaData!: IMedia[];
  public useOriginalUrl: boolean = false;
  public readonly isMobileView = signal<boolean>(false);

  // dependencies
  private readonly dialogRef = inject(DialogRef);
  public readonly data: {
    title: string;
    mediaData: IMedia[];
    useOriginalUrl: boolean;
    initialMedia?: IMedia;
  } = inject(DIALOG_DATA);

  constructor() {
    // dialog title
    this.title = this.data?.title;
    this.mediaData = this.data?.mediaData;
    this.useOriginalUrl = this.data?.useOriginalUrl;
    this._setMobileState();
  }

  @HostListener('window:resize')
  onResize() {
    this._setMobileState();
  }

  ngOnInit(): void {
    const initialMedia = this.data?.initialMedia;
    this.selectedProductMedia =
      initialMedia != null &&
      this.mediaData?.some(media => this._isSameMedia(media, initialMedia))
        ? initialMedia
        : this.mediaData?.[0];
  }

  public onSelectMainImage(media: IMedia) {
    this.selectedProductMedia = media;
  }

  public close() {
    this.dialogRef.close();
  }

  private _setMobileState() {
    this.isMobileView.set(window.matchMedia('(max-width: 47.99em)').matches);
  }

  private _isSameMedia(a: IMedia, b: IMedia): boolean {
    if (a._id != null && b._id != null) {
      return a._id === b._id;
    }
    return a.url === b.url;
  }
}
